import { useImmer } from "use-immer";

import { AnalyticsAudienceType } from "@intelligems/ig-types/schemas/analytics/base";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { getControlVariationId } from "@src/utils/controlVariation";
import { UseQueryResult } from "@tanstack/react-query";

import { MobileAnalyticsFrame } from "../../shared/components/MobileAnalyticsFrame";
import { MobileAudienceTableContent } from "../components/MobileAudienceTableContent";

interface AnalyticsSegmentTableWrapperProps {
  experience: ExperienceFe;
  variations: VariationFe[];
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  testDuration: number;
  metricFilterIndex?: number;
}

const tableTabs = [
  { title: "All Visitors", id: "0" },
  { title: "Desktop/Mobile", id: "1" },
  { title: "New/Returning", id: "2" },
  { title: "Source Channels", id: "3" },
  { title: "Source Sites", id: "4" },
  { title: "Top 10 Countries", id: "5" },
  { title: "Top 10 Landing Pages", id: "6" },
];

const tabValueToQueryType: Record<string, AnalyticsAudienceType> = {
  "1": "device_type",
  "2": "visitor_type",
  "3": "source_channel",
  "4": "source_site",
  "5": "country_code",
  "6": "landing_page_full_path",
};

export const MobileAudienceTable = ({
  experience,
  variations,
  dashboardData,
  graphConfigs,
  testDuration,
}: AnalyticsSegmentTableWrapperProps) => {
  const [tabValue, setTabValue] = useImmer("0");
  const tabValueInt = parseInt(tabValue);
  const controlVariationId = getControlVariationId(variations);
  const formattedGraphConfigs: GraphConfig[] = graphConfigs.map((config) => ({
    ...config,
    datasetId: "variation_audience",
  }));

  const segmentDataQuery: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] =
    useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: formattedGraphConfigs,
        experience,
        audience: tabValueToQueryType[tabValue] ?? "device_type",
        topN: [5, 6].includes(tabValueInt) ? 10 : undefined,
        overviewDataLoaded: dashboardData[0].isSuccess,
        enabled: tabValueInt !== 0,
      },
      "experiences"
    );

  return (
    <MobileAnalyticsFrame
      title="By Audience"
      selectedValue={tabValue}
      onSelectionChange={setTabValue}
      dropdownOptions={tableTabs}
    >
      <MobileAudienceTableContent
        variations={variations}
        dashboardData={dashboardData}
        segmentDataQuery={segmentDataQuery}
        graphConfigs={graphConfigs}
        testDuration={testDuration}
        tabValue={tabValueInt}
        controlVariationId={controlVariationId}
      />
    </MobileAnalyticsFrame>
  );
};
